import type {ContentFAQ, ContentHomeResponse} from '@/ts/types/dto/home-dto';
import type {Content} from '@/ts/types/dto/interventions-dto';
import type {SideBarGroupedSection, SideBarSectionItem} from '@/ts/types/component/balm-sidebar';
import {groupByCategory} from '@/ts/composables/pure/use-content-sidebar';
import {featuredIntroVideo} from '@/ts/composables/pure/use-intro-video';

// The player reads an empty poster or alt as "none", which is what a null field means here. The
// file is passed in rather than read off the content, so an entry can only be built from a piece
// that has one.
function videoEntry(content: Content, src: string, label: string): SideBarSectionItem {
  return {
    id: content.id,
    label,
    video: {
      src,
      posterSrc: content.image ?? '',
      posterAlt: content.name ?? '',
    },
  };
}

/**
 * The intro video as the FAQ's business, or null while it is still the home page's.
 *
 * The flag says there is featured content and that the video is no longer offered; it does not say
 * the first piece carries a video, which is what an entry needs. The reading below is what decides
 * that, so a flag raised over a piece with no file leaves the FAQ as it was rather than showing an
 * entry that cannot play.
 */
export function faqIntroVideo(home: ContentHomeResponse | null | undefined): Content | null {
  return home?.introVideoInFaq ? featuredIntroVideo(home) : null;
}

/**
 * The FAQ groups, with the intro video opening the first one. The group is chosen by position
 * rather than by name because the names are authored per language in the CMS, and a video with no
 * group to join gets one of its own, nameless so the entry is not announced twice.
 */
export function buildFaqSections(
  faq: ContentFAQ[],
  introVideo: Content | null,
  videoLabel: string,
): SideBarGroupedSection[] {
  const groups = groupByCategory(faq, item => ({
    id: item.id,
    html: item.answer,
    label: item.question,
  }));

  if (!introVideo?.video) {
    return groups;
  }

  const entry = videoEntry(introVideo, introVideo.video, videoLabel);
  if (groups.length === 0) {
    return [{id: introVideo.id, name: '', items: [entry]}];
  }

  const [first, ...rest] = groups;
  return [{...first, items: [entry, ...first.items]}, ...rest];
}
